import { ElementType } from 'components/element-viewer/index';
import useBundleTranslation from 'i18n';
import { Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import DropdownIcon from 'components/common/dropdown-icon/DropdownIcon';
import styles from './InfoIcon.styles';
import { ExternalReportViewerDataType } from 'app/extreport/ExternalReportViewer';
import { nl2br } from 'tools/tools';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import { IElementData, IElementDataSettings } from 'components/common/element-data/types';
import { useElementMetadataSettings } from 'hooks/useElementMetadata';
import ElementDataInfo from 'components/common/element-data/ElementDataInfo';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import isOwnerVisible from 'tools/owners';

export default function InfoIcon({
    element,
    viewerRequestExtraData,
}: {
    element: ElementType;
    viewerRequestExtraData?: ExternalReportViewerDataType;
}) {
    const { t } = useBundleTranslation(['components/common/element']);

    const [infoBtnBottomCoordinate, setInfoBtnBottomCoordinate] = useState<number>(0);
    const elementDataContainerRef = React.useRef<HTMLDivElement>(null);
    const { configuration }: UserAuth = useAppSelector((state) => state.auth);

    const hasIcon = useEmbeddingContextVisible('control_buttons.info');

    let metadataSettings = useElementMetadataSettings('info');
    const elementData: IElementData = {
        elementType: element.row.type,
        certifiedInd: element.info.certified,
        certificationLevelColor: element.certification?.color ?? 'success.main',
        certificationLevelName: element.certification?.name,
        lastCertifiedByName: element.info.certifiedByDn,
        lastCertifiedTime: element.info.certifiedDate,
        descriptionMarkdownInd: element.row.descriptionMarkdownInd,
        descriptionMarkdown: element.row.descriptionMarkdown,
        description: element.row.description.trim().length > 0 ? nl2br(element.row.description) : '',
        descriptionMaxRows: 11,
        topics: element.topics,
        businessOwner: element.info.businessOwnerDn,
        businessOwnerEmail: element.info.businessOwnerEmail,
        dataSteward: element.info.dataStewardDn,
        dataStewardEmail: element.info.dataStewardEmail,
        technicalOwner: element.info.technicalOwnerDn,
        technicalOwnerEmail: element.info.technicalOwnerEmail,
        dataSourceName: viewerRequestExtraData?.additionalData.dataSourceName,
        engagement: element.info.engagement,
        lastDisplayGenerationTime:
            element.row.lastDisplayGenerationTime && element.row.lastDisplayGenerationTime != '0000-00-00 00:00:00'
                ? element.row.lastDisplayGenerationTime
                : '',
        categoryPath: element.info.categoryNamePath,
        customFields: element.info.customFields,
    };

    if (metadataSettings.fields['thumbnail']?.display) {
        metadataSettings.fields['thumbnail'].display = false;
    }

    ['business_owner', 'technical_owner', 'data_steward'].forEach((ownerField) => {
        if (metadataSettings.fields[ownerField]?.display) {
            metadataSettings.fields[ownerField].display =
                isOwnerVisible((configuration.owners as any)[ownerField], 'elements') &&
                isOwnerVisible((configuration.owners as any)[ownerField], 'tile_preview');
        }
    });

    const elementDataSettings: IElementDataSettings = {
        metadata: metadataSettings,
    };

    const infoIconRef = React.useRef<HTMLDivElement>(null);

    const defineInfoHeight = () => {
        if (infoIconRef.current) {
            const infoIconRect = infoIconRef.current.getBoundingClientRect();
            setInfoBtnBottomCoordinate(infoIconRect.bottom + 15);
        }
    };

    useEffect(() => {
        defineInfoHeight();
        window.addEventListener('resize', defineInfoHeight);

        return () => {
            window.removeEventListener('resize', defineInfoHeight);
        };
    }, []);

    if (!hasIcon) {
        return null;
    }

    return (
        <span data-test={'top-info-icon'} ref={infoIconRef}>
            <DropdownIcon
                tooltip={t('info')}
                icon="info-circle"
                popperSx={{ maxHeight: `calc(100% - ${infoBtnBottomCoordinate}px)`, display: 'flex' }}
                onClickInside={(event, handleClose) => {
                    if (
                        elementDataContainerRef.current &&
                        elementDataContainerRef.current.contains(event.target as HTMLElement)
                    ) {
                        if (
                            event.target.closest('.element-description-expand-control') ||
                            event.target.closest('.element-data-glossary-terms-header')
                        ) {
                            return;
                        }
                    }
                    handleClose(event);
                }}
                transitionTimeout={{
                    appear: 500,
                    enter: 300,
                    exit: 0,
                }}
            >
                <Stack sx={styles.tooltipContainer} ref={elementDataContainerRef}>
                    <ElementDataInfo
                        elementData={elementData}
                        elementDataSettings={elementDataSettings}
                        showHeader={elementDataSettings.metadata.settings.showSectionHeaders}
                    />
                </Stack>
            </DropdownIcon>
        </span>
    );
}
